import React, { ChangeEvent } from 'react';
import styled from 'styled-components';
// --------------------------------------------------------------------------------
import { String, StringProps } from './String';
import { CheckBox } from './UserInputs/CheckBox';
import { FileUpload } from './UserInputs/FileUpload';
import { SelectDropDown } from './UserInputs/SelectDropDown';
import { DatePicker } from './UserInputs/DatePicker';
import { DropDownProps } from './DropDown';
// --------------------------------------------------------------------------------
import { config } from '../index';

export interface InputProps {
  /* Input error props */
  error?: ErrorProps;
  /* Input label */
  label?: StringProps;
  /* Input dropdown prop config */
  dropdownConfig?: DropDownProps;
  /* Input default value */
  value?: string;
  /* Input name */
  name?: string; // input element will be ignored if name pop not provided
  /* Input placeholder */
  placeholder?: string;
  /* Input type */
  type?: string;
  /* Input height */
  height?: string;
  /* Input value max length */
  maxLength?: number;
  /* Input border radius */
  borderRadius?: string;
  /* Input margin */
  margin?: string;
  /* Input padding */
  padding?: string;
  /* Input width */
  width?: string;
  /* Input background color */
  backgroundColor?: string;
  /* Input font size */
  fontSize?: string;
  /* Input order. As rendered in UI */
  order?: number;
  /* Input additional class props */
  className?: string;
  /* Input character length counter */
  isCounter?: boolean;
  /* Input border */
  border?: string;
  /* Input border bottom */
  borderBottom?: string;
  /* Input border top */
  borderTop?: string;
  /* Input border left */
  borderLeft?: string;
  /* Input border right */
  borderRight?: string;
  /* Input min height. Typical for text area inputs */
  minHeight?: string;
  /* Input onChange handler */
  onChange?: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | any>
  ) => void;
  /* Input onFocus callBack handler */
  onFocus?: () => void;
}

// extend InputProps for error and label properties
export interface ErrorProps extends StringProps {
  bottom?: string;
  top?: string;
  left?: string;
  right?: string;
  backgroundColor?: string;
  borderRadius?: string;
  border?: string;
  padding?: string;
  height?: string;
  type?: string;
  isTextInput?: boolean;
}

const StyledInput = styled.input<InputProps>`
  position: relative;
  outline: none;
  box-sizing: border-box;
  padding: 0 ${config.spacingXSmall};
  width: 100%;

  &::placeholder {
    color: ${config.grey};
    font-size: ${config.fontXMedium};
  }

  font-size: ${(props) => props.fontSize};
  height: ${(props) => props.height};
  border: ${(props) => props.border};
  border-radius: ${(props) => props.borderRadius};
  background-color: ${(props) => props.backgroundColor};
`;

const StyledTextAreaInput = styled.textarea<InputProps>`
  position: relative;
  outline: none;
  box-sizing: border-box;
  padding: 0 ${config.spacingXSmall};
  width: 100%;
  resize: vertical;
  font: inherit;

  &::placeholder {
    color: ${config.muted};
    font-size: ${config.fontSmall};
  }

  font-size: ${(props) => props.fontSize};
  border: ${(props) => props.border};
  border-radius: ${(props) => props.borderRadius};
  background-color: ${(props) => props.backgroundColor};
  min-height: ${(props) => props.minHeight};
`;

const StyledInputWrapper = styled.div<InputProps>`
  display: flex;
  flex-wrap: wrap;
  position: relative;

  background-color: ${(props) => props.backgroundColor};
  border: ${(props) => props.border};
  border-top: ${(props) => props.borderTop};
  border-bottom: ${(props) => props.borderBottom};
  border-left: ${(props) => props.borderLeft};
  border-right: ${(props) => props.borderRight};
  width: ${(props) => props.width};
`;

const StyledErrorMessageWrapper = styled.div<InputProps>`
  position: absolute;
  z-index: 1;
  cursor: default;
  display: flex;
  align-items: center;
  right: 0;

  top: ${(props) => (props.type === 'textarea' ? '34px' : 'auto')};
  padding: ${(props) => props.padding};
  margin: ${(props) => props.margin};
  bottom: ${(props) => (props.type === 'textarea' ? 'auto' : '0')};
`;

const StyledErrorMessage = styled.div<ErrorProps>`
  display: flex;
  align-items: center;
  justify-content: center;

  background-color: ${(props) => props.backgroundColor || config.transparent};
  border-radius: ${(props) => props.borderRadius || 'unset'};
  height: ${(props) => props.height};
`;

const StyledCounter = styled.div<ErrorProps>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  min-width: 72px;
  cursor: default;
  display: flex;
  align-items: center;
  bottom: -${config.spacingXSmall};
  right: ${config.spacingSmall};
  background-color: ${config.orangeYellow};
  border-radius: ${config.spacingXSmall};
`;

const StyledInputContainer = styled.div<InputProps>`
  position: relative;
  width: 100%;
  margin: ${(props) => props.margin};
  padding: ${(props) => props.padding};
`;

// 📌 Primary UI component for user interaction
export const Input: React.FC<InputProps> = ({
  error,
  dropdownConfig,
  // --------------------------------------------------------------------------------
  type = 'text',
  placeholder,
  value,
  label,
  name,
  height = config.spacingXXLarge,
  maxLength = 100,
  borderRadius = config.spacingSmall,
  margin = 'unset',
  padding = 'unset',
  backgroundColor = config.transparent,
  width = '100%',
  border = `1px solid ${config.muted}`,
  borderTop = 'none',
  borderBottom = 'none',
  borderLeft = 'none',
  borderRight = 'none',
  isCounter = true,
  fontSize = config.fontMedium,
  minHeight = '100px',
  // --------------------------------------------------------------------------------
  onChange,
  // additional props
  ...props
}) => {
  let isTextInput =
    type === 'text' ||
    type === 'password' ||
    type === 'email' ||
    type === 'number' ||
    type === 'tel' ||
    type === 'search' ||
    type === 'url' ||
    type === 'time' ||
    type === 'datetime' ||
    type === 'datetime-local' ||
    type === 'month' ||
    type === 'week' ||
    type === 'color' ||
    type === 'range';
  let isTextArea = type === 'textarea';
  let isCheckBox = type === 'checkbox';
  let isSelect = type === 'select';
  let isFile = type === 'file';
  let isDatePicker = type === 'date';

  let isTextType = isTextArea || isTextInput || isSelect;

  // if input name not specified then default to use label
  if (!name) name = label?.string;

  return (
    <StyledInputWrapper
      width={width}
      // input border properties
      borderTop={borderTop}
      borderBottom={borderBottom}
      borderLeft={borderLeft}
      borderRight={borderRight}
      {...props}
    >
      <StyledInputContainer margin={margin} padding={padding}>
        {!isCheckBox && label && (
          <String
            color={config.darkMuted}
            fontSize={config.fontMedium}
            fontWeight="600"
            margin={`0 0 ${config.spacingMedium} 0`}
            width="100%"
            // --------------------------------------------------------------------------------
            {...label}
          />
        )}
        {isTextInput && (
          <StyledInput
            placeholder={placeholder}
            name={name}
            fontSize={fontSize}
            maxLength={maxLength}
            border={border}
            borderRadius={borderRadius}
            backgroundColor={backgroundColor}
            height={height}
            value={onChange ? value : undefined}
            type={type}
            // --------------------------------------------------------------------------------
            onChange={onChange}
          />
        )}
        {isTextArea && (
          <StyledTextAreaInput
            placeholder={placeholder}
            name={name}
            fontSize={fontSize}
            maxLength={maxLength}
            border={border}
            borderRadius={borderRadius}
            backgroundColor={backgroundColor}
            minHeight={minHeight}
            value={onChange ? value : undefined}
            // --------------------------------------------------------------------------------
            onChange={onChange}
          />
        )}

        {isSelect && (
          <SelectDropDown
            dropdownConfig={dropdownConfig}
            name={name}
            placeholder={placeholder}
            fontSize={`calc(${fontSize} - 2px)`} // reduce font size for dropdown
            border={border}
            borderRadius={borderRadius}
            backgroundColor={backgroundColor}
            height={`calc(${height} - 2px})`} // accommodates font dropdown by 2px
            value={onChange ? value : undefined}
            // --------------------------------------------------------------------------------
            onChange={onChange}
            {...props}
          />
        )}

        {isCheckBox && (
          <CheckBox
            // borderRadius={borderRadius} // 👉 border radius for checkbox
            name={name}
            label={label}
            onChange={onChange}
          />
        )}

        {isFile && (
          <FileUpload
            borderRadius={borderRadius}
            label={label}
            name={name}
            onChange={onChange}
          />
        )}

        {isDatePicker && (
          <DatePicker
            borderRadius={borderRadius}
            label={label}
            name={name}
            onChange={onChange}
          />
        )}
      </StyledInputContainer>

      {error && (
        <StyledErrorMessageWrapper
          margin={margin}
          padding={padding}
          type={type}
          height={height}
        >
          <StyledErrorMessage
            backgroundColor={config.cedarChest}
            borderRadius={borderRadius}
            height={height}
          >
            <String
              color={config.white}
              fontSize={config.fontXSmall}
              padding={config.spacingXSmall}
              limit={1}
              fontWeight="600"
              {...error}
            />
          </StyledErrorMessage>
        </StyledErrorMessageWrapper>
      )}

      {isCounter && isTextType && !isSelect && (
        <StyledCounter>
          <String
            string={
              value
                ? `Max Characters: ${maxLength - (value + '')?.length}` // + '' converts undefined | number to string ''
                : undefined
            }
            color={config.white}
            fontSize="8px"
            padding="1px 4px"
            limit={1}
            fontWeight="400"
          />
        </StyledCounter>
      )}
    </StyledInputWrapper>
  );
};
